// React imports
import { forwardRef, useEffect, useMemo, useState, useImperativeHandle } from 'react';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';

// Generic imports
import CurrencyList from 'currency-list';
import _ from 'lodash';
import { formatDisplayDate } from '../../utils/date.js';


function DisplayCurrencySettings(props) {
    const { item, setItem, organization, exchangeRates, lockedExchangeRates, displayCurrencies, preface } = props;

    useEffect(() => {
        if (_.isString(item.displayCurrency)) {
            setItem({ ...item, displayCurrency: { to: item.displayCurrency, source: item.displayCurrencySource || 'fixed', convert: true } });
        } else if (!item.displayCurrency) {
            setItem({ ...item, displayCurrency: { to: organization.defaults?.currency, source: 'live', convert: false } });
        }
    }, [ item ]);

    const enabled = item.displayCurrency?.convert || false;

    const options = useMemo(() => {
        return Object.keys(CurrencyList.getAll('en_US')).map(currency => currency);
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Switch
                    checked={item.displayCurrency?.convert || false}
                    onChange={(e) => setItem({ ...item, displayCurrency: { ...item.displayCurrency, to: item.displayCurrency?.to || organization.defaults?.currency, convert: e.target.checked }})}
                />
                <Typography>Convert on Display</Typography>
            </Box>

            <Typography variant='caption' component={Box} sx={{ opacity: enabled ? 1 : 0.5 }}>
                {preface}
            </Typography>

            <Typography variant='body2' component={Box} sx={{ marginTop: '16px' }}>
                Use Currency Exchange Source From:
            </Typography>

            <RadioGroup 
                value={item.displayCurrency?.source || 'live'} 
                onChange={(e) => setItem({ ...item, displayCurrency: { ...item.displayCurrency, source: e.target.value }})}
                row
                disabled={!enabled}
            >
                <FormControlLabel
                    control={<Radio size='small' />}
                    value='live'
                    label={<Box sx={{ fontSize: '13px' }}>Live Currency Conversions from Market Data</Box>}
                    disabled={!enabled}
                />
                <FormControlLabel
                    control={<Radio size='small' />}
                    value='fixed'
                    label={<Box sx={{ fontSize: '13px' }}>Workspace Settings (Fallback to Market Data)</Box>}
                    disabled={!enabled}
                />
            </RadioGroup>

            <Typography variant='body2' component={Box} sx={{ mb: 1 , mt: 1 }}>
                Currency:
            </Typography>

            <Autocomplete
                sx={{ flex: 1 }}
                options={options}
                disabled={!enabled}
                getOptionLabel={option => `${CurrencyList.getAll('en_US')[option].name} (${option}, ${CurrencyList.getAll('en_US')[option].symbol})`}
                renderInput={(params) => <TextField {...params} />}
                value={item.displayCurrency?.to || (_.isString(item.displayCurrency) ? item.displayCurrency : null)}
                onChange={(e, value) => {
                    setItem({ ...item, displayCurrency: { ...item.displayCurrency, to: value } });
                }}
            />

            {enabled && !_.isEmpty(displayCurrencies) && (
                <Typography variant='caption' component={Box} sx={{ marginTop: '16px' }}>
                    <Typography variant='caption' component={Box} sx={{ fontWeight: '800' }}>
                        Conversion rates used according to organization settings:
                    </Typography>
                    {displayCurrencies.map(currency => {
                        const organizationRate = _.find(organization.exchangeRates, { from: currency, to: item.displayCurrency?.to });
                        const lockedRateOrganization = _.find(lockedExchangeRates?.organization, { from: currency, to: item.displayCurrency?.to });
                        const lockedRateLive = lockedExchangeRates?.live ? lockedExchangeRates?.live[item.displayCurrency?.to] / lockedExchangeRates?.live[currency] : null;

                        function displayNum(num) {
                            return Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(num);
                        }

                        if (item.displayCurrency?.source === 'fixed' && lockedRateOrganization) {
                            return (
                                <Box key={currency}>
                                    1 {currency} = {displayNum(lockedRateOrganization?.rate)} {item.displayCurrency?.to} (Locked Workspace Settings, last updated {formatDisplayDate(lockedRateOrganization?.updatedAt)})
                                </Box>
                            )
                        } else if (item.displayCurrency?.source === 'fixed' && organizationRate) {
                            return (
                                <Box key={currency}>
                                    1 {currency} = {displayNum(organizationRate.rate)} {item.displayCurrency?.to} (Workspace Settings, last updated {formatDisplayDate(organizationRate.updatedAt)})
                                </Box>
                            )
                        } else if (item.displayCurrency?.source === 'live' && lockedRateLive) {
                            return (
                                <Box key={currency}>
                                    1 {currency} = {displayNum(lockedRateLive)} {item.displayCurrency?.to} (Locked Market Data)
                                </Box>
                            )
                        } else {
                            return (
                                <Box key={currency}>
                                    1 {currency} = {displayNum(exchangeRates[item.displayCurrency?.to] / exchangeRates[currency])} {item.displayCurrency?.to} (Market Data)
                                </Box>
                            )
                        }
                    })}
                </Typography>
            )}

            {enabled && (
                <Alert severity='info' size={'small'} variant='outlined' sx={{ marginTop: '16px', fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                    Conversion rates are locked automatically when offer is sent to customer, or manually in the offer settings
                </Alert>
            )}

            {enabled && (
                <Alert severity='success' size={'small'} variant='outlined' sx={{ marginTop: '8px', fontSize: '11px' }}>
                    <b>Tip:</b> To use a custom exchange rate (e.g., round 1.1784 to 1.18), set your preferred rate in <b>Workspace Settings → Exchange Rates</b> and select <b>"Workspace Settings"</b> as the source above. This lets you control the exact rate used for this and future quotes.
                </Alert>
            )}

            {/* Exchange rates reference */}
            {(() => {
                const defaultCur = organization.defaults?.currency || 'USD';
                const extraCurrencies = (organization.defaults?.extraCurrencies || '').split(',').filter(Boolean);
                const allCurrencies = _.uniq([
                    defaultCur,
                    ...extraCurrencies,
                    ...(displayCurrencies || []),
                ]).filter(Boolean).sort();
                if (allCurrencies.length < 2) return null;
                const fmt = (num) => Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(num);
                // Build all pairs
                const pairs = [];
                for (let i = 0; i < allCurrencies.length; i++) {
                    for (let j = 0; j < allCurrencies.length; j++) {
                        if (i !== j) pairs.push({ from: allCurrencies[i], to: allCurrencies[j] });
                    }
                }
                return (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                            Exchange Rates Reference
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {pairs.map(({ from, to }) => {
                                const orgRate = _.find(organization.exchangeRates, { from, to }) || _.find(organization.exchangeRates, { from: to, to: from });
                                const orgValue = orgRate ? (orgRate.from === from ? orgRate.rate : 1 / orgRate.rate) : null;
                                const liveValue = exchangeRates?.[to] && exchangeRates?.[from] ? exchangeRates[to] / exchangeRates[from] : null;
                                return (
                                    <Box key={`${from}-${to}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#64748b', minWidth: '90px' }}>
                                            1 {from} → {to}
                                        </Typography>
                                        {orgValue && (
                                            <Typography sx={{ fontSize: '11px', color: '#1e40af' }}>
                                                {fmt(orgValue)} <span style={{ color: '#94a3b8' }}>(workspace{orgRate?.updatedAt ? `, ${formatDisplayDate(orgRate.updatedAt)}` : ''})</span>
                                            </Typography>
                                        )}
                                        {orgValue && liveValue && <Typography sx={{ fontSize: '11px', color: '#cbd5e1' }}>|</Typography>}
                                        {liveValue && (
                                            <Typography sx={{ fontSize: '11px', color: '#059669' }}>
                                                {fmt(liveValue)} <span style={{ color: '#94a3b8' }}>(live)</span>
                                            </Typography>
                                        )}
                                        {!orgValue && !liveValue && (
                                            <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>N/A</Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })()}
        </Box>
    )
}

function DisplayCurrencySettingsField(props) {
    const { field, setField, organization, exchangeRates, lockedExchangeRates } = props;

    const displayCurrencies = _(field.values).map(v => v.currency).uniq().without(field.displayCurrency?.to).compact().value();

    return (
        <DisplayCurrencySettings
            item={field}
            setItem={setField}
            organization={organization}
            exchangeRates={exchangeRates}
            lockedExchangeRates={lockedExchangeRates}
            displayCurrencies={displayCurrencies}
            preface={'When displaying, convert all values to the following currency:'}
        />
    )
}

function DisplayCurrencySettingsOffer(props) {
    const { offer, setOffer, organization, exchangeRates, lockedExchangeRates } = props;

    const displayCurrencies = _(offer.sections).map(s => s.offers).flatten().map(o => o.fields).flatten().map(f => _.values(f.values)).flatten().map(v => v.currency).uniq().without(offer.displayCurrency?.to).compact().value();

    return (
        <DisplayCurrencySettings 
            item={offer} 
            setItem={setOffer} 
            organization={organization} 
            exchangeRates={exchangeRates} 
            lockedExchangeRates={lockedExchangeRates} 
            displayCurrencies={displayCurrencies} 
            preface={'When displaying, convert the offer total to the following currency:'}
        />
    )
}
function DisplayCurrencySettingsSection(props) {
    const { section, setSection, organization, exchangeRates, lockedExchangeRates } = props;

    const displayCurrencies = _(section.offers).map(o => o.fields).flatten().map(f => _.values(f.values)).flatten().map(v => v.currency).uniq().without(section.displayCurrency?.to).compact().value();

    return (
        <DisplayCurrencySettings 
            item={section} 
            setItem={setSection} 
            organization={organization} 
            exchangeRates={exchangeRates} 
            lockedExchangeRates={lockedExchangeRates} 
            displayCurrencies={displayCurrencies} 
            preface={'When displaying, convert the section total to the following currency:'}
        />
    )
}

const BulkUpdateSettings = forwardRef((props, ref) => {
    const { field, section, offer } = props;
    const [ bulkUpdate, setBulkUpdate ] = useState(false);
    const [ bulkUpdateType, setBulkUpdateType ] = useState('section');

    useImperativeHandle(ref, () => ({
        get: () => {
            if (!bulkUpdate) return false;
            if (field) return false;
            if (section) return true;
            if (offer) return true;
            return false;
        }
    }));

    if (field) return null;

    return (
        <Alert severity='info' variant='outlined' icon={false} size={'small'} sx={{ marginTop: '16px', fontSize: '11px !important' }}>
            <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px', fontWeight: '400' }}>
                <FormControlLabel
                    sx={{ marginLeft: '0px' }}
                    control={<Switch
                        size={'small'}
                        checked={bulkUpdate}
                        onChange={(e) => setBulkUpdate(e.target.checked)}
                    />}
                    label={section ? <Box sx={{ fontSize: '11px' }}>Bulk Update - Update all fields in this section</Box> :
                        <Box sx={{ fontSize: '11px' }}>Bulk Update - Update all sections and fields in this offer</Box>
                    }
                />
            </AlertTitle>
        </Alert>
    )
});

export { DisplayCurrencySettingsField, DisplayCurrencySettingsOffer, DisplayCurrencySettingsSection, BulkUpdateSettings };